import { MouseEventHandler, forwardRef } from 'react'
import styled from 'styled-components'
import { Border, Colors, Spacing } from '../../styles'
import { pointerQuery } from '../../styles/dimensions'
import { Motion } from '../../styles/motion'
import { EdgeHighlight } from '../atoms/SelectableContainer'

const SelectionEdgeHighlight = styled(EdgeHighlight)<{ $visible: boolean; $animate: boolean }>`
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    ${({ $animate }) => $animate && `transition: opacity ${Motion.exit};`}
`

const ItemContainerDiv = styled.div<{
    isSelected?: boolean
    isMultiSelected?: boolean
    isCompact?: boolean
    forceHoverStyle?: boolean
    $animateSelection?: boolean
}>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    box-sizing: border-box;
    background-color: ${({ isSelected }) => (isSelected ? Colors.background.sub : Colors.background.white)};
    border: ${Border.stroke.medium} solid ${({ isSelected }) => (isSelected ? Colors.background.border : 'transparent')};
    border-radius: ${Border.radius.medium};
    ${({ $animateSelection }) =>
        $animateSelection && `transition: background-color ${Motion.exit}, border-color ${Motion.exit};`}
    /* Touch browsers leave :hover stuck on the last-tapped element, which would read as a selected
       row. Nothing is gated behind this highlight, so scope it to real hover and give touch the
       same feedback while the row is actually held instead. */
    ${pointerQuery.canHover} {
        :hover {
            background-color: ${Colors.background.sub};
            border-color: ${Colors.background.border};
        }
    }
    ${pointerQuery.noHover} {
        :active {
            background-color: ${Colors.background.sub};
        }
    }

    ${({ forceHoverStyle }) =>
        forceHoverStyle &&
        `
        background-color: ${Colors.background.sub};
        `}
    ${({ isMultiSelected }) =>
        isMultiSelected &&
        `
        border-color: ${Colors.control.primary.stroke};
        :hover {
            border-color: ${Colors.control.primary.stroke};
        }
    `}
    padding: 0 ${({ isCompact }) => (isCompact ? `${Spacing._4} 0 0` : Spacing._16)};
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

interface ItemContainerProps {
    isSelected?: boolean
    isMultiSelected?: boolean
    isCompact?: boolean
    onClick?: MouseEventHandler
    children: React.ReactNode
    forceHoverStyle?: boolean
    className?: string
    animateSelection?: boolean
}
const ItemContainer = forwardRef<HTMLDivElement, ItemContainerProps>(
    (
        {
            isSelected,
            isMultiSelected = false,
            isCompact = false,
            onClick,
            children,
            forceHoverStyle,
            className,
            animateSelection,
        },
        ref
    ) => (
        <ItemContainerDiv
            isSelected={isSelected}
            isMultiSelected={isMultiSelected}
            isCompact={isCompact}
            onClick={onClick}
            ref={ref}
            forceHoverStyle={forceHoverStyle}
            className={className}
            $animateSelection={animateSelection}
            data-animate-selection={animateSelection ? '' : undefined}
            data-multi-selected={isMultiSelected ? '' : undefined}
        >
            {(isSelected || animateSelection) && (
                <SelectionEdgeHighlight
                    color={Colors.control.primary.stroke}
                    $visible={Boolean(isSelected)}
                    $animate={Boolean(animateSelection)}
                    data-selection-edge=""
                />
            )}
            {children}
        </ItemContainerDiv>
    )
)

export default ItemContainer
